import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EditorHeader } from "./EditorHeader";
import { toast } from "sonner";
import { Loader2, Upload, RefreshCw, CheckCircle2, Trash2 } from "lucide-react";
import { StatementChat } from "@/components/StatementChat";

type StatementRow = {
  id: string;
  file_name: string;
  file_type: string;
  file_url: string;
  status: string;
  period_start: string | null;
  period_end: string | null;
  parse_summary: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
};

type TxnRow = {
  id: string;
  txn_date: string;
  description_clean: string | null;
  counterparty: string | null;
  amount: number;
  currency: string;
  direction: "in" | "out";
  category: string;
  subcategory: string | null;
  balance_after: number | null;
  confidence: number | null;
};

const CATEGORIES = ["investment", "ahmed_investment", "revenue", "burn", "transfer", "fee", "refund", "other"];

export function BankStatementsEditor() {

  const [statements, setStatements] = useState<StatementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ parsed: number; total: number } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [txns, setTxns] = useState<TxnRow[]>([]);
  const [txnsLoading, setTxnsLoading] = useState(false);

  const loadStatements = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("bank_statements")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setStatements((data as StatementRow[]) ?? []);
    setLoading(false);
  };

  const loadTxns = async (statementId: string) => {
    setTxnsLoading(true);
    // Page through results — PostgREST caps responses at 1000 rows by default
    const PAGE = 1000;
    const all: TxnRow[] = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase
        .from("transactions")
        .select("id, txn_date, description_clean, counterparty, amount, currency, direction, category, subcategory, balance_after, confidence")
        .eq("statement_id", statementId)
        .order("txn_date", { ascending: true })
        .range(from, from + PAGE - 1);
      if (error) { toast.error(error.message); break; }
      const chunk = (data as TxnRow[]) ?? [];
      all.push(...chunk);
      if (chunk.length < PAGE) break;
    }
    setTxns(all);
    setTxnsLoading(false);
  };


  useEffect(() => { loadStatements(); }, []);
  useEffect(() => { if (selected) loadTxns(selected); }, [selected]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setProgress(null);
    let pollHandle: ReturnType<typeof setInterval> | null = null;
    try {
      const ext = file.name.split(".").pop()?.toLowerCase();
      const type = ext === "csv" ? "csv" : ext === "pdf" ? "pdf" : "xlsx";
      const path = `${Date.now()}-${file.name.replace(/[^\w.-]/g, "_")}`;
      const { error: upErr } = await supabase.storage.from("bank_statements").upload(path, file);
      if (upErr) throw upErr;
      const { data: { user } } = await supabase.auth.getUser();
      const { data: stmt, error: insErr } = await supabase
        .from("bank_statements")
        .insert({ file_name: file.name, file_type: type, file_url: path, uploaded_by: user?.id, status: "pending" })
        .select().single();
      if (insErr) throw insErr;
      toast.success("Uploaded, parsing…");

      // Poll for progress while parse runs
      pollHandle = setInterval(async () => {
        const { data } = await supabase
          .from("bank_statements")
          .select("parse_summary, status")
          .eq("id", stmt.id)
          .maybeSingle();
        const p = (data?.parse_summary as { progress?: { parsed: number; total: number } } | null)?.progress;
        if (p && typeof p.parsed === "number" && typeof p.total === "number") setProgress(p);
      }, 800);

      const { data, error } = await supabase.functions.invoke("parse-statement", {
        body: { statement_id: stmt.id },
      });
      if (error) throw error;
      toast.success(`Parsed ${((data as { summary?: { rows: number } })?.summary?.rows) ?? 0} rows`);
      await loadStatements();
      setSelected(stmt.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      if (pollHandle) clearInterval(pollHandle);
      setProgress(null);
      setUploading(false);
    }
  };

  const reparse = async (id: string) => {
    const { error } = await supabase.functions.invoke("parse-statement", { body: { statement_id: id } });
    if (error) { toast.error(error.message); return; }
    toast.success("Re-parsed");
    await loadStatements();
    if (selected === id) await loadTxns(id);
  };

  const commit = async (id: string) => {
    const { error } = await supabase.from("bank_statements").update({ status: "committed" }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Committed");
    await loadStatements();
  };

  const remove = async (s: StatementRow) => {
    if (!confirm(`Delete "${s.file_name}" and all its transactions? This cannot be undone.`)) return;
    const { error: txErr } = await supabase.from("transactions").delete().eq("statement_id", s.id);
    if (txErr) { toast.error(txErr.message); return; }
    const { error: stErr } = await supabase.from("bank_statements").delete().eq("id", s.id);
    if (stErr) { toast.error(stErr.message); return; }
    if (s.file_url) {
      await supabase.storage.from("bank_statements").remove([s.file_url]);
    }
    toast.success("Deleted");
    if (selected === s.id) { setSelected(null); setTxns([]); }
    await loadStatements();
  };

  const updateTxn = async (id: string, patch: Partial<TxnRow>) => {
    setTxns((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const { error } = await supabase.from("transactions").update(patch as never).eq("id", id);
    if (error) toast.error(error.message);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <EditorHeader title="Bank Statements" description="Upload, parse, and commit bank statements" />


      <Card>
        <CardHeader><CardTitle>Upload statement</CardTitle></CardHeader>
        <CardContent>
          <label className="flex items-center gap-3">
            <Input
              type="file"
              accept=".csv,.xlsx,.xls,.pdf"
              disabled={uploading}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.currentTarget.value = ""; }}
            />
            {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
          </label>
          {uploading && (
            <div className="mt-3 space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {progress
                    ? `Parsing transactions… ${progress.parsed.toLocaleString()} / ${progress.total.toLocaleString()}`
                    : "Uploading and reading file…"}
                </span>
                {progress && progress.total > 0 && (
                  <span>{Math.round((progress.parsed / progress.total) * 100)}%</span>
                )}
              </div>
              <div className="h-2 w-full rounded bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{
                    width: progress && progress.total > 0
                      ? `${Math.min(100, (progress.parsed / progress.total) * 100)}%`
                      : "15%",
                  }}
                />
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            XLSX/CSV supported now (SNB format). PDF coming soon.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Statements</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : statements.length === 0 ? (
            <p className="text-sm text-muted-foreground">No statements yet.</p>
          ) : (
            <div className="space-y-2">
              {statements.map((s) => {
                const summary = s.parse_summary as { rows?: number; inflow?: number; outflow?: number } | null;
                return (
                  <div key={s.id} className={`border rounded p-3 ${selected === s.id ? "border-primary" : ""}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{s.file_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {s.period_start ?? "?"} → {s.period_end ?? "?"} · {summary?.rows ?? 0} rows
                          {summary?.inflow != null && ` · in ${summary.inflow.toLocaleString()} / out ${summary.outflow?.toLocaleString()}`}
                        </div>
                        {s.error_message && <div className="text-xs text-destructive mt-1">{s.error_message}</div>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={s.status === "committed" ? "default" : s.status === "failed" ? "destructive" : "secondary"}>
                          {s.status}
                        </Badge>
                        <Button size="sm" variant="outline" onClick={() => setSelected(s.id)}>View</Button>
                        <Button size="sm" variant="ghost" onClick={() => reparse(s.id)} title="Re-parse">
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        {s.status === "parsed" && (
                          <Button size="sm" onClick={() => commit(s.id)}>
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Commit
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => remove(s)} title="Delete" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {selected && (
        <Card>
          <CardHeader><CardTitle>Transactions preview</CardTitle></CardHeader>
          <CardContent>
            {txnsLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-2 w-12 text-right text-muted-foreground">#</th>
                      <th className="p-2">Date</th>
                      <th className="p-2">Counterparty</th>
                      <th className="p-2">Category</th>
                      <th className="p-2 text-right">Amount</th>
                      <th className="p-2 text-right">Balance</th>
                      <th className="p-2" title="Confidence — how sure the parser is about the auto-assigned category (100% = certain).">
                        Confidence
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t, idx) => (
                      <tr key={t.id} className="border-b">
                        <td className="p-2 text-right text-muted-foreground tabular-nums">{idx + 1}</td>
                        <td className="p-2 whitespace-nowrap">{t.txn_date}</td>
                        <td className="p-2 max-w-[280px]">
                          <Input
                            value={t.counterparty ?? ""}
                            onChange={(e) => setTxns((rows) => rows.map((r) => r.id === t.id ? { ...r, counterparty: e.target.value } : r))}
                            onBlur={(e) => updateTxn(t.id, { counterparty: e.target.value })}
                            className="h-7 text-xs"
                          />
                        </td>
                        <td className="p-2">
                          <select
                            value={t.category}
                            onChange={(e) => updateTxn(t.id, { category: e.target.value })}
                            className="h-7 text-xs border rounded px-1 bg-background"
                          >
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td className={`p-2 text-right whitespace-nowrap ${t.direction === "in" ? "text-green-600" : ""}`}>
                          {t.direction === "out" ? "-" : ""}{Number(t.amount).toLocaleString()} {t.currency}
                        </td>
                        <td className="p-2 text-right whitespace-nowrap text-muted-foreground">
                          {t.balance_after != null ? Number(t.balance_after).toLocaleString() : "—"}
                        </td>
                        <td className="p-2">{t.confidence != null ? `${Math.round(t.confidence * 100)}%` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <StatementChat
        endpoint="statement-admin-ai"
        title="AI assistant — categorize, mask, fix"
        placeholder={selected ? "e.g. 'Mark all SADAD payments as fee', 'Mask the name Mohammed Ali as Investor A', 'Categorize transactions containing PAYROLL as burn'." : "Select a statement above to focus the assistant, or ask globally."}
        suggestions={[
          "Show me uncategorized transactions",
          "Mask 'John Doe' as Investor A",
          "Categorize all transactions containing 'SALARY' as burn / payroll",
          "Add a rule: any description with 'STC' is fee / telecom",
        ]}
        extraBody={selected ? { statement_id: selected } : {}}
        onAfterReply={() => { if (selected) loadTxns(selected); loadStatements(); }}
      />
    </div>
  );
}
